import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ComponentKey, Organization } from "../data/types";
import { useChartFilterStore } from "./useChartFilterStore";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrgState {
  organizations: Organization[];
  activeOrgId: number | null;
  activeFacilityId: number; // currently selected facility — drives all API queries
  activeFacilityName: string | undefined; // normalized (lowercase, trimmed) name of the active facility
  availableComponents: ComponentKey[];

  setOrganizations: (orgs: Organization[]) => void;

  // ── Sets the active org AND auto-selects its first facility ──────────────
  // Call this when user expands an org in the sidebar or navigates to an org page.
  setActiveOrg: (orgId: number) => void;

  // ── Explicitly switch facility within the active org ──────────────────────
  // Called by the facility switcher dropdown in the header.
  setActiveFacility: (facilityId: number) => void;

  setAvailableComponents: (components: ComponentKey[]) => void;
  clearAll: () => void;
}

export const useOrganizationStore = create<OrgState>()(
  persist(
    (set, get) => ({
      organizations: [],
      activeOrgId: null,
      activeFacilityId: 0,
      activeFacilityName: undefined,
      availableComponents: [],

      // Sort organizations by id in ascending order so the list is deterministic regardless of API response order.
      // Also re-derives activeFacilityName from this fresh data (never trusts
      // a stale cached value) — but deliberately leaves activeFacilityId
      // untouched even when it no longer resolves to anything (e.g. that
      // facility was just deactivated and the backend stopped returning it).
      // The currently-rendered page is still requesting data for that exact
      // id; silently redirecting it to a different facility here would mean
      // the page's own "this facility is deactivated" handling (see
      // isFacilityDeactivatedError) never gets a chance to run. Only the
      // *displayed name* falls back to undefined — see FacilitySwitcher in
      // Header.tsx for how the header itself shows a sensible label instead
      // of blank when this happens.
      setOrganizations: (organizations) => {
        const { activeFacilityId } = get();
        const sorted = [...organizations].sort(
          (orgA, orgB) => orgA.id - orgB.id,
        );

        const activeFacility = sorted
          .flatMap((org) => org.facilities)
          .find((fac) => fac.id === activeFacilityId);

        set({
          organizations: sorted,
          activeFacilityName: activeFacility?.name?.toLowerCase().trim(),
        });
      },

      setActiveOrg: (orgId) => {
        const { organizations, activeFacilityId: prevFacilityId } = get();
        const org = organizations.find((o) => o.id === orgId);

        // Auto-select first facility when switching orgs
        const firstFacility = org?.facilities?.[0];
        const nextFacilityId = firstFacility?.id ?? 0;

        set({
          activeOrgId: orgId,
          activeFacilityId: nextFacilityId,
          activeFacilityName: firstFacility?.name?.toLowerCase().trim(),
        });
        useChartFilterStore.getState().switchFacilityFilters(prevFacilityId, nextFacilityId);
      },

      setActiveFacility: (facilityId) => {
        const { organizations, activeFacilityId } = get();
        if (facilityId === activeFacilityId) return;
        const facility = organizations
          .flatMap((org) => org.facilities)
          .find((fac) => fac.id === facilityId);

        set({
          activeFacilityId: facilityId,
          activeFacilityName: facility?.name?.toLowerCase().trim(),
        });
        useChartFilterStore.getState().switchFacilityFilters(activeFacilityId, facilityId);
      },

      setAvailableComponents: (components) =>
        set({ availableComponents: components }),

      clearAll: () =>
        set({
          organizations: [],
          activeOrgId: null,
          activeFacilityId: 0,
          activeFacilityName: undefined,
          availableComponents: [],
        }),
    }),
    { name: "organization-store" },
  ),
);
